import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchApiWithToken, hydraNextPageParam } from '~/api/utils'
import type {
  ApplicationInfosLDResponse,
  ApplicationInfosResponse
} from '../types'

export type ApplicationsFilters = {
  userId?: number | number[]
  agencyId?: number
  orderId?: number
  offerId?: number
  isCompleted?: boolean
  isArchived?: boolean | 'all'
  archivedOffer?: 'all' | 'only' | 'exclude'
}

export const useApplications = (
  filters: ApplicationsFilters = {},
  enabled = true,
  itemsPerPage = 30
): {
  applications: ApplicationInfosResponse[] | undefined
  applicationsTotal: number | undefined
  isApplicationsLoading: boolean
  isApplicationsError: boolean
  fetchNextApplicationPage: () => void
  hasNextApplicationPage: boolean
  isFetchingNextApplicationPage: boolean
} => {
  const {
    data,
    isLoading: isApplicationsLoading,
    isError: isApplicationsError,
    fetchNextPage: fetchNextApplicationPage,
    hasNextPage: hasNextApplicationPage,
    isFetchingNextPage: isFetchingNextApplicationPage
  } = useInfiniteQuery<ApplicationInfosLDResponse, Error>({
    queryKey: ['applications', filters, itemsPerPage],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(itemsPerPage),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        itemsPerPage: String(itemsPerPage),
        'order[receivedAt]': 'desc'
      })

      if (filters.orderId) {
        params.append('offer.order', String(filters.orderId))
      }

      if (filters.offerId) {
        params.append('offer', String(filters.offerId))
      }

      if (filters.userId) {
        if (Array.isArray(filters.userId)) {
          params.append('offer.user', filters.userId.join(','))
        } else {
          params.append('offer.user', String(filters.userId))
        }
      }

      if (filters.agencyId) {
        params.append('offer.agency', String(filters.agencyId))
      }

      if (filters.isCompleted !== undefined) {
        params.append('isCompleted', String(filters.isCompleted))
      }

      if (filters.isArchived !== undefined) {
        params.append('isArchived', String(filters.isArchived))
      }

      if (filters.archivedOffer === 'all' || filters.archivedOffer === 'only') {
        params.append('archivedOffer', filters.archivedOffer)
      }

      return await fetchApiWithToken<ApplicationInfosLDResponse>({
        method: 'get',
        url: `/v1/applications?${params}`,
        useLDJson: true
      })
    },
    refetchOnWindowFocus: false,
    enabled:
      enabled &&
      (!!filters.orderId ||
        !!filters.offerId ||
        !!filters.userId ||
        !!filters.agencyId),
    staleTime: 300_000
  })

  return {
    applications: data?.pages.flatMap(
      (page: ApplicationInfosLDResponse) => page['hydra:member']
    ),
    applicationsTotal: data?.pages[0]?.['hydra:totalItems'],
    isApplicationsLoading,
    isApplicationsError,
    fetchNextApplicationPage,
    hasNextApplicationPage,
    isFetchingNextApplicationPage
  }
}
